import * as React from 'react';
import { Radio, RadioGroup } from 'react-radio-group';

import { Ajax } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { VehiclesService } from '@shared/vehicles/VehiclesService';
import { CollapsiblePanel } from '@shared/ui-react/CollapsiblePanel';
import { FormGroup } from '@shared/ui-react/UtilComponents';
import { findSameValueProps } from '@shared/utils/utils-object';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
import { LogpointsDownloadDialog } from '@shared/logpoints/ui/LogpointsDownloadDialog';
import { Button } from '@shared/ui-react/Button';
import { FiletreeSelectReact } from "@shared/ui-react/FiletreeSelectReact";
import { LogpointFeatureFactory } from '@openlayers/logpoints/LogpointFeatureFactory';
import { Tooltip } from 'primereact/tooltip';
import { Message } from 'primereact/message';


export class LogpointsEditor extends React.Component<{
    logpoints: ILogpoint[],
    treeData: ITreeData,
    onClose: () => void,
    onSaveSuccess: (updatedIds: number[], updatedLogpoints: ILogpoint[], formData: any) => void,
    onDeleteSuccess: (deletedIds: number[]) => void
}, {}> {
    state = {
        vehicles: null, // for vehicle selection dropdown
        isLoading: false,

        // form
        approvalStage: 'DONT_CHANGE',
        folderId: -1,
        folderChecked: false,
        modelId: -1,
        modelChecked: false,
        alignmentId: -1,
        alignmentChecked: false,
        vehicleId: -1,
        code: '',
        pointName: '',
        number: '',
        linecode: '',
        surfacecode: '',
    };

    elems: {
        form?: HTMLFormElement,
    } = {};

    save = (isMultiEdit: boolean) => {
        const formData: any = {
            logpointIds: this.props.logpoints.map(l => l.id),
        };

        $(this.elems.form).serializeArray().forEach(field => { // serializeArray will ignore disabled fields
            formData[field.name] = field.value;
        });

        const updateFormData = (key: string, checked: boolean, id: number) => {
            if ((!isMultiEdit || checked) && id) formData[key] = id;
        };

        updateFormData('folderId', !!this.state.folderChecked, this.state.folderId);
        updateFormData('modelId', !!this.state.modelChecked, this.state.modelId);
        updateFormData('alignmentId', !!this.state.alignmentChecked, this.state.alignmentId);

        const total = this.props.logpoints.length;
        const stats = this.getApprovalStats();
        if (stats.approvalDowngrade + stats.approvalUpgrade > 0) {
            if (!confirm(__('js_logpoints.approval.confirm'))) {
                return;
            }
        }

        if (formData.approvalStage == 'DONT_CHANGE') {
            delete formData.approvalStage;
        }

        this.setState({ isLoading: true });

        Ajax({
            url: 'logpoints_bulk_edit.json',
            method: 'POST',
            data: formData,
        }).fail(Notify.error)
            .always(() => this.setState({ isLoading: false }))
            .done(({ updatedIds, updatedLogpoints }) => {
                LogpointFeatureFactory.clearCache();
                this.props.onSaveSuccess(updatedIds, updatedLogpoints, formData);
                Notify.success(null, 20);
                this.forceUpdate();
            });
    };

    handleDelete = () => {
        if (!confirm(__('js_logpoint.delete_confirm'))) return;
        this.setState({ isLoading: true });

        const logpointIds = this.props.logpoints.map(l => l.id);

        Ajax({
            url: '/api/1/logpoint/delete_by_ids.json',
            method: 'POST',
            data: {
                logpointIds,
            },
        }).fail(Notify.error)
            .always(() => this.setState({ isLoading: false }))
            .done(() => {
                this.setState({
                    logpoints: [],
                });
                Notify.success();
                this.props.onDeleteSuccess(logpointIds);
            });
    };

    componentDidMount() {
        this.setState({ isLoading: true });

        VehiclesService.fetchForActiveProject()
            .always(() => this.setState({ isLoading: false }))
            .done(vehicles => {
                this.setState({
                    vehicles,
                });
                this.setDefaultFormValues();
            });
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.logpoints != prevProps.logpoints) {
            this.setDefaultFormValues();
        }
    }

    getApprovalStats = () => {
        const numMeasured = this.props.logpoints.filter(f => !f.approvalStage || f.approvalStage === 'MEASURED').length;
        const numApproved = this.props.logpoints.filter(f => f.approvalStage === 'APPROVED').length;
        const numChecked = this.props.logpoints.filter(f => f.approvalStage === 'CHECKED').length;
        const newApproval = this.state.approvalStage;

        let approvalUpgrade = 0;
        let approvalDowngrade = 0;
        if (newApproval === 'MEASURED') {
            approvalUpgrade = 0;
            approvalDowngrade = numChecked;
        }
        else if (newApproval === 'APPROVED') {
            approvalUpgrade = numChecked;
            approvalDowngrade = 0;
        }
        else if (newApproval === 'CHECKED') {
            approvalUpgrade = numMeasured;
            approvalDowngrade = numApproved;
        }
        return {
            approvalUpgrade,
            approvalDowngrade,
            numMeasured,
            numApproved,
            numChecked
        }
    }

    render() {
        const { logpoints } = this.props;

        let sumDz = 0;
        for (const logpoint of logpoints) {
            sumDz += logpoint.modelDeltaZ;
        }
        const avgDz = (sumDz / logpoints.length).toFixed(4);
        const numInTolerance = logpoints.filter(f => f.tol == 2 || f.tol == 0).length;

        const canEdit = kuura.hasPrivilege('LOGPOINTS_PAGE');
        const isMultiEdit = logpoints.length > 1;
        const sdDz = this.calcStandardDeviation(logpoints.filter(l => l.hasOwnProperty('modelDeltaZ')).map(l => l.modelDeltaZ));
        const { approvalUpgrade, approvalDowngrade, numMeasured, numApproved, numChecked } = this.getApprovalStats();

        return (
            <div id='logpoints-bulk-wrapper'>
                <header className='padded'>
                    <CollapsiblePanel label={__('general.selected_x') + ': ' + logpoints.length} defaultIsOpen={true}>
                        <table>
                            <tbody>
                                <tr>
                                    <td>{__('js_map.logpoints.average_dz')}:</td>
                                    <td><strong>{$.isNumeric(avgDz) ? avgDz : ''}</strong></td>
                                </tr>
                                <tr>
                                    <td>{__('js_map.logpoints.dz_standard_deviation')}:</td>
                                    <td><strong>{$.isNumeric(sdDz) ? sdDz.toFixed(1) : ''}</strong></td>
                                </tr>
                                <tr>
                                    <td>{__('js_map.logpoints.in_tolerance')}:</td>
                                    <td><strong>{numInTolerance} / {logpoints.length}</strong></td>
                                </tr>
                                <tr>
                                    <td>{__('js_logpoints.approval.measured')}:</td>
                                    <td><strong>{numMeasured} / {logpoints.length}</strong></td>
                                </tr>
                                <tr>
                                    <td>{__('js_logpoints.approval.checked')}:</td>
                                    <td><strong>{numChecked} / {logpoints.length}</strong></td>
                                </tr>
                                <tr>
                                    <td>{__('js_logpoints.approval.approved')}:</td>
                                    <td><strong>{numApproved} / {logpoints.length}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </CollapsiblePanel>

                    {kuura.hasPrivilege('Download_LogPoint') && <div style={{ position: 'absolute', top: 20, right: 20 }}>
                        <Button onClick={this.download}><i className='iki iki-download' /></Button>
                    </div> }
                </header>

                <form ref={e => this.elems.form = e} onSubmit={e => e.preventDefault()} className={this.state.isLoading ? 'overlay-loading' : null}>
                    <div className='form-group'>
                        <Tooltip target=".logpoint-approval-tooltip" baseZIndex={99999}>
                            <div>{__('js_logpoints.approval.help_tooltip')}</div>
                        </Tooltip>
                        <label>{__('js_logpoints.column_stage')} <i className="iki iki-help logpoint-approval-tooltip" style={{ fontSize: '1.1em' }}/></label>

                        <RadioGroup name='approvalStage' selectedValue={this.state.approvalStage}
                            onChange={val => this.setState({ approvalStage: val })}>
                            <label><Radio value='DONT_CHANGE' /> {__('general.dont_change')}</label>
                            <label><Radio value='MEASURED' /> {__('js_logpoints.approval.measured')}</label>
                            <label><Radio
                                value='CHECKED' /> {__('js_logpoints.approval.checked')} {numChecked === logpoints.length && (`(${__('general.current_value')})`)}
                            </label>
                            <label><Radio value='APPROVED' /> {__('js_logpoints.approval.approved')}</label>
                        </RadioGroup>

                        { approvalDowngrade > 0 && <div className='alert alert-warning'>
                            <Message text={__('js_logpoints.approval.downgrade_warning', approvalDowngrade)} severity='warn'/>
                        </div> }
                        { approvalUpgrade > 0 && <div>
                            <Message text={__('js_logpoints.approval.upgrade_warning', approvalUpgrade)} severity='success' />
                        </div> }
                        { this.state.approvalStage !== 'DONT_CHANGE' && (approvalDowngrade + approvalUpgrade === 0) && <div>
                            <Message text={__('js_logpoints.approval.no_changes_will_be_made')} severity='info'/>
                        </div> }
                    </div>

                    {canEdit && <div>
                        <FormGroup checkable={isMultiEdit}
                            checked={this.state.folderChecked}
                            onChecked={folderChecked => this.setState({ folderChecked })}
                            tight={true}
                            title={__('general.folder')}>
                            <FiletreeSelectReact selectable="folder"
                                includeRoot={false}
                                treeData={this.props.treeData}
                                selectedId={this.state.folderId}
                                onChange={folderId => this.setState({ folderId })}
                                headerControlsFilterOnly={true}
                            />
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit}
                            checked={this.state.modelChecked}
                            onChecked={modelChecked => this.setState({ modelChecked })}
                            tight={true}
                            title={__('general.model')}>
                            <FiletreeSelectReact selectable="file"
                                includeRoot={false}
                                treeData={this.props.treeData}
                                childFilter={LogpointsService.filetreeFilterModels}
                                canClearValue={true}
                                selectedId={this.state.modelId}
                                onChange={modelId => this.setState({ modelId: modelId || -1 })}
                                hideEmptyFolders={true}
                                storageKey='logpoint-editor-model-tree'
                            />
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit}
                            checked={this.state.alignmentChecked}
                            onChecked={alignmentChecked => this.setState({ alignmentChecked })}
                            tight={true}
                            title={__('general.alignment')}>
                            <FiletreeSelectReact selectable="file"
                                includeRoot={false}
                                treeData={this.props.treeData}
                                childFilter={LogpointsService.filetreeFilterAlignments}
                                canClearValue={true}
                                selectedId={this.state.alignmentId}
                                onChange={alignmentId => this.setState({ alignmentId: alignmentId || -1 })}
                                hideEmptyFolders={true}
                                storageKey='logpoint-editor-alignment-tree'
                            />
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit} tight={true} title={__('general.vehicle')}>
                            <select name='vehicleId' value={this.state.vehicleId}
                                onChange={e => this.setState({ vehicleId: e.currentTarget.value })}>
                                <option value='-1'>{__('js_logpoints.edit.no_vehicle')}</option>
                                {this.state.vehicles && this.state.vehicles.map(v => <option value={v.id} key={v.id}>{v.name}</option>)}
                            </select>
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit} tight={true} title={__('js_logpoints.column_linecode')}>
                            <input name='linecode' type='text'
                                placeholder={this.state.linecode == 'multiple values' ? __('general.multiple_values') : null}
                                value={this.state.linecode == 'multiple values' ? '' : this.state.linecode}
                                onChange={e => this.setState({ linecode: e.currentTarget.value })} />
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit} tight={true} title={__('js_logpoints.column_point_number')}>
                            <input name='number' type='text'
                                placeholder={this.state.number == 'multiple values' ? __('general.multiple_values') : null}
                                value={this.state.number == 'multiple values' ? '' : this.state.number}
                                onChange={e => this.setState({ number: e.currentTarget.value })} />
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit} tight={true} title={__('js_logpoints.column_point_name')}>
                            <input name='pointName' type='text'
                                placeholder={this.state.pointName == 'multiple values' ? __('general.multiple_values') : null}
                                value={this.state.pointName == 'multiple values' ? '' : this.state.pointName}
                                onChange={e => this.setState({ pointName: e.currentTarget.value })} />
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit} tight={true} title={__('js_logpoints.column_code')}>
                            <input name='code' type='text' list='projectPointCodes'
                                placeholder={this.state.code == 'multiple values' ? __('general.multiple_values') : null}
                                value={this.state.code == 'multiple values' ? '' : this.state.code}
                                onChange={e => this.setState({ code: e.currentTarget.value })} />
                            <datalist id='projectPointCodes'>
                                {ProjectCodesService.getCodesArrayByType('P').map(c => <option value={c}>{ProjectCodesService.codeWithDescription(c)}</option>)}
                                {ProjectCodesService.getCodesArrayByType('L').map(c => <option value={c}>{ProjectCodesService.codeWithDescription(c)}</option>)}
                            </datalist>
                            <div>{ProjectCodesService.getDescription(this.state.code)}</div>
                        </FormGroup>

                        <FormGroup checkable={isMultiEdit} tight={true} title={__('js_logpoints.column_surfacecode')}>
                            <input name='surfacecode' type='text' list='projectSurfaceCodes'
                                placeholder={this.state.surfacecode == 'multiple values' ? __('general.multiple_values') : null}
                                value={this.state.surfacecode == 'multiple values' ? '' : this.state.surfacecode}
                                onChange={e => this.setState({ surfacecode: e.currentTarget.value })} />
                            <datalist id='projectSurfaceCodes'>
                                {ProjectCodesService.getCodesArrayByType('S').map(c => <option value={c}>{ProjectCodesService.codeWithDescription(c)}</option>)}
                            </datalist>
                            <div>{ProjectCodesService.getDescription(this.state.surfacecode)}</div>
                        </FormGroup>
                    </div>}
                </form>

                <footer>
                    {canEdit &&
                        <button className='btn-danger' onClick={this.handleDelete} title={kuura.getMessage('general.delete')}><i
                            className='iki iki-trash'></i></button>
                    }
                    <div style={{ float: 'right' }} className='btn-group'>
                        <button onClick={this.props.onClose} type='button'>{kuura.getMessage('general.close')}</button>
                        <button onClick={() => this.save(isMultiEdit)} ref='save' className='btn-primary'
                            disabled={this.state.isLoading}>{kuura.getMessage('general.save')}</button>
                    </div>
                </footer>
            </div>
        );
    }

    setDefaultFormValues() {
        const { logpoints } = this.props;
        let state: any = {
            folderId: -1,
            folderChecked: false,
            modelId: -1,
            modelChecked: false,
            alignmentId: -1,
            alignmentChecked: false,
            vehicleId: -1,
            code: '',
            linecode: '',
            surfacecode: '',
            pointName: '',
            number: '',
            approvalStage: 'DONT_CHANGE',
        };

        if (logpoints.length == 1) {
            const logpoint = logpoints[0];
            state = {
                ...state,
                ...logpoint,
                approvalStage: logpoint.approvalStage || 'MEASURED',
            };
        }
        else if (logpoints.length > 1) {
            const commonValues = findSameValueProps(logpoints, 'folderId', 'modelId', 'alignmentId', 'vehicleId', 'code', 'linecode', 'surfacecode', 'number', 'pointName');
            state = {
                ...state,
                ...commonValues,
                approvalStage: 'DONT_CHANGE',
            };
        }

        this.setState(state);
    }

    download = () => {
        LogpointsDownloadDialog.open(this.props.logpoints.map(l => l.id));
    }

    calcStandardDeviation = (array: number[]) => {
        if (!array || !array.length) return 0;
        const n = array.length;
        const mean = array.reduce((a, b) => a + b) / n;
        return Math.sqrt(array.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n);
    };
}
